import React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

export type SectionPreviewItem = {
  id: string;
  name: string;
  subsections?: { id: string; name: string }[];
};

interface SectionsPreviewProps {
  title?: string;
  isLoading: boolean;
  errorMessage?: string;
  items: SectionPreviewItem[];
}

export function SectionsPreview({ title = "Sections Preview", isLoading, errorMessage, items }: SectionsPreviewProps) {
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const ns = new Set(prev);
      if (ns.has(id)) ns.delete(id);
      else ns.add(id);
      return ns;
    });
  };

  const Heading = (
    <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
      <div className="w-1 h-4 rounded-full" style={{ backgroundColor: "var(--color-primary)" }}></div>
      {title}
    </h4>
  );

  if (isLoading) {
    return (
      <div>
        {Heading}
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse" />
                  <div className="flex-1">
                    <div className="h-4 w-40 bg-gray-200 rounded animate-pulse mb-2" />
                    <div className="h-3 w-28 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="h-6 w-16 bg-gray-200 rounded-full animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div>
        {Heading}
        <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg">
          Unable to load sections preview.
          <span className="block text-[11px] text-red-500 mt-1">{errorMessage}</span>
        </div>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div>
        {Heading}
        <div className="text-xs text-gray-500">Structure details are not available for this template yet.</div>
      </div>
    );
  }

  return (
    <div>
      {Heading}
      <div className="space-y-2">
        {items.map((section, idx) => {
          const hasSubsections = (section.subsections?.length ?? 0) > 0;
          const isExpanded = expanded.has(section.id);
          return (
            <div key={section.id} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <div
                className={`p-4 transition-all duration-200 ${hasSubsections
                  ? "cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:border-indigo-200 hover:shadow-md"
                  : "cursor-default"
                  }`}
                onClick={() => hasSubsections && toggle(section.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-sm"
                        style={{ backgroundColor: "var(--color-primary)" }}
                      >
                        {idx + 1}
                      </div>
                      {hasSubsections && (
                        <div className="flex-shrink-0">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 transition-transform duration-200" style={{ color: "var(--color-primary)" }} />
                          ) : (
                            <ChevronRight className="w-4 h-4 transition-transform duration-200" style={{ color: "var(--color-primary)" }} />
                          )}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 mb-0.5">{section.name}</p>
                        <p className="text-xs text-gray-500">
                          {hasSubsections ? `${section.subsections?.length} subsections` : "Single section"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 font-medium">
                      {hasSubsections ? section.subsections?.length : 1} parts
                    </span>
                  </div>
                </div>
              </div>
              {hasSubsections && isExpanded && (
                <div className="border-t border-gray-100 bg-gradient-to-r from-gray-50 to-blue-50/30">
                  <div className="px-6 py-4 space-y-3">
                    {section.subsections?.map((sub) => (
                      <div key={sub.id} className="flex items-center gap-3 py-2 px-3 bg-white/60 rounded-lg border border-gray-100">
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0 shadow-sm"
                          style={{ backgroundColor: "var(--color-secondary)" }}
                        ></div>
                        <span className="text-sm text-gray-700 flex-1 font-medium">{sub.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SectionsPreview;
